import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from '@/components/icons';
import { cn } from '@/lib/utils';

export interface SideDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  badge?: React.ReactNode;
  header?: React.ReactNode;
  headerActions?: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
  className?: string;
  contentClassName?: string;
  hideCloseButton?: boolean;
}

const sizeClasses: Record<NonNullable<SideDrawerProps['size']>, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  full: 'max-w-full',
};

export const SideDrawer: React.FC<SideDrawerProps> = ({
  isOpen,
  onClose,
  title,
  description,
  icon,
  badge,
  header,
  headerActions,
  footer,
  children,
  size = 'xl',
  className,
  contentClassName,
  hideCloseButton = false,
}) => {
  const { t } = useTranslation();
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 overflow-hidden"
    >
      {/* Backdrop */}
      <div
        onClick={onClose}
        aria-hidden="true"
        className="fixed inset-0 bg-black/50 backdrop-blur-xs transition-opacity animate-in fade-in duration-150 print:hidden"
      />

      {/* Slide-over panel container */}
      <div className="fixed inset-y-0 right-0 max-w-full flex pl-10 pointer-events-none print:static print:inset-auto print:p-0 print:block">
        <div
          className={cn(
            'pointer-events-auto w-screen bg-card text-card-foreground border-l border-border shadow-2xl h-full flex flex-col justify-between overflow-hidden animate-in slide-in-from-right duration-200',
            sizeClasses[size],
            className
          )}
        >
          {/* Header */}
          {header ? (
            <div className="px-6 py-4 border-b border-border bg-card shrink-0 flex items-center justify-between gap-3 print:hidden">
              <div className="flex-1 min-w-0">{header}</div>
              {!hideCloseButton && (
                <button
                  type="button"
                  onClick={onClose}
                  aria-label={t('common.close_drawer', 'Close drawer')}
                  className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors cursor-pointer shrink-0"
                >
                  <X size={18} />
                </button>
              )}
            </div>
          ) : title ? (
            <div className="px-6 py-4 border-b border-border bg-card shrink-0 flex items-center justify-between gap-3 print:hidden">
              <div className="flex items-center gap-3 min-w-0">
                {icon && (
                  <div className="h-9 w-9 rounded-lg bg-primary/10 border border-primary/20 text-primary flex items-center justify-center shrink-0">
                    {icon}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h2 className="text-base font-bold text-foreground tracking-tight truncate">
                      {title}
                    </h2>
                    {badge}
                  </div>
                  {description && (
                    <p className="text-xs text-muted-foreground truncate">
                      {description}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                {headerActions}
                {!hideCloseButton && (
                  <button
                    type="button"
                    onClick={onClose}
                    aria-label={t('common.close_drawer', 'Close drawer')}
                    className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors cursor-pointer"
                  >
                    <X size={18} />
                  </button>
                )}
              </div>
            </div>
          ) : null}

          {/* Body Content */}
          <div
            className={cn(
              'flex-1 overflow-y-auto p-6 space-y-6',
              contentClassName
            )}
          >
            {children}
          </div>

          {/* Footer */}
          {footer && (
            <div className="px-6 py-4 border-t border-border bg-card/90 backdrop-blur-xs shrink-0 flex items-center justify-between gap-3 print:hidden">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const Drawer = SideDrawer;
export type DrawerProps = SideDrawerProps;
